import { useEffect, useRef, useState } from "react";
import ReactPlayer from "react-player";
import { useNavigate } from "react-router-dom";
import "../styles/QuizPopup.css";
import { logout } from "../../../../utils/authSession";
import { lessonsURLServices } from "../../../../services/lessons/lessonsURLServices";
import { toPositiveId } from "../../../../utils/ids";

// Renew this many seconds before the signed cookie/URL actually expires (Phase 3
// requirement: refresh ~30s before expiry, never after).
const RENEWAL_BUFFER_SECONDS = 30;

export default function LessonPlayerPopup(props) {
	const [videoUrl, setVideoUrl] = useState("");
	const [errorMessage, setErrorMessage] = useState("");
	const navigate = useNavigate();
	const renewalTimerRef = useRef(null);

	useEffect(() => {
		let isCancelled = false;

		// the backend authorizes against the real parent course id: never request playback
		// with a missing/0 course or video id
		if (
			toPositiveId(props.courseId) === null ||
			toPositiveId(props.videoId) === null
		) {
			setErrorMessage("Video unavailable.");
			return undefined;
		}

		const fetchAndScheduleRenewal = () => {
			lessonsURLServices(props.courseId, props.lessonId, props.videoId)
				.then((data) => {
					if (isCancelled) return;
					setVideoUrl(data.videoUrl);
					setErrorMessage("");
					const renewInMs =
						Math.max(data.expiresIn - RENEWAL_BUFFER_SECONDS, 0) * 1000;
					renewalTimerRef.current = setTimeout(
						fetchAndScheduleRenewal,
						renewInMs
					);
				})
				.catch((error) => {
					if (isCancelled) return;
					const status = error?.response?.status;
					if (status === 401) {
						logout();
						navigate("/login");
					} else if (status === 403) {
						setErrorMessage(
							"You are not authorized to access this video."
						);
					} else if (status === 404) {
						setErrorMessage("Video unavailable.");
					} else {
						setErrorMessage(
							"Something went wrong loading this video. Please try again."
						);
					}
				});
		};

		fetchAndScheduleRenewal();

		// Clears the pending renewal timer whenever this effect re-runs (video
		// change - courseId/lessonId/videoId changed) and on unmount. LessonsCards
		// only mounts this component while its popup is open, so unmount already
		// covers "popup close"; logging out navigates away from this screen
		// entirely (existing app behavior), which also unmounts this component -
		// no separate logout listener is needed here.
		return () => {
			isCancelled = true;
			if (renewalTimerRef.current) {
				clearTimeout(renewalTimerRef.current);
				renewalTimerRef.current = null;
			}
		};
	}, [props.courseId, props.lessonId, props.videoId, navigate]);

	if (errorMessage) {
		return <p className="LessonPlayerErrorMessage">{errorMessage}</p>;
	}

	return (
		<ReactPlayer
			config={{ file: { attributes: { controlsList: "nodownload" } } }} // disables download button
			onContextMenu={(e) => e.preventDefault()} // disables right click
			url={videoUrl}
			controls
			width='50%'
			height='auto'
		/>
	);
}
